import { ArrowLeft, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import adminWhatsAppService from '../../services/adminWhatsAppService';
import {
  WHATSAPP_CAMPAIGN_STATUS as C,
  CAMPAIGN_STATUS_COUNT_ORDER,
  WHATSAPP_MESSAGE_STATUS,
  WHATSAPP_RETRY_FAILED_CODE,
  formatShortDate,
} from '../../utils/whatsapp';
import Modal from '../ui/Modal';
import CampaignStatusBadge from './CampaignStatusBadge';
import RecipientsTable from './RecipientsTable';

const PAGE_SIZE = 50;
const POLL_MS = 5000;

const CONFIRMS = {
  cancel: {
    title: 'Cancel this campaign?',
    body: 'Messages that have not been sent yet will not be sent. Messages already sent are not affected.',
    keep: 'Keep campaign',
    go: 'Yes, cancel campaign',
    tone: 'bg-red-600 hover:bg-red-700',
  },
  uncertain: {
    title: 'Retry messages that may already be delivered?',
    body: 'These messages failed in a way that does not tell us whether WhatsApp delivered them. Retrying can send the same person the same message twice.',
    keep: 'Not now',
    go: 'Retry anyway',
    tone: 'bg-gray-900 hover:bg-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600',
  },
};

/**
 * One campaign: header with status and counts, the per-recipient table, and the actions that belong
 * here (retry failed, cancel). Sending happens in the New campaign wizard, never here. A `sending`
 * campaign refreshes itself every 5 seconds, except while the browser tab is hidden.
 * @param {{campaignId: string, onBack: () => void, onChanged?: () => void}} props
 */
const CampaignDetail = ({ campaignId, onBack, onChanged }) => {
  const toast = useToast();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [uncertainLeft, setUncertainLeft] = useState(0);
  const [uncertainMessage, setUncertainMessage] = useState('');
  const [confirm, setConfirm] = useState(null);
  const latest = useRef(0);
  const pollFailed = useRef(false);

  // Only the newest request may write state; unmounting supersedes anything still in flight.
  useEffect(
    () => () => {
      latest.current += 1;
    },
    [],
  );

  const load = useCallback(
    async ({ silent = false } = {}) => {
      const ticket = ++latest.current;
      if (!silent) setLoading(true);
      try {
        const data = await adminWhatsAppService.getCampaign(campaignId, {
          status: statusFilter || undefined,
          page,
          limit: PAGE_SIZE,
        });
        if (ticket !== latest.current) return;
        setDetail(data);
        setError('');
        pollFailed.current = false;
      } catch (err) {
        if (ticket !== latest.current) return;
        if (silent) {
          // A background refresh keeps what is on screen; say so once, not every 5 seconds.
          if (!pollFailed.current) toast.error(err.message);
          pollFailed.current = true;
        } else {
          setError(err.message);
          toast.error(err.message);
        }
      } finally {
        if (ticket === latest.current) setLoading(false);
      }
    },
    [campaignId, statusFilter, page, toast],
  );

  useEffect(() => {
    load();
  }, [load]);

  const sending = detail?.status === C.SENDING;
  useEffect(() => {
    if (!sending) return undefined;
    let timer = null;
    const stop = () => {
      if (timer !== null) clearInterval(timer);
      timer = null;
    };
    const start = () => {
      stop();
      timer = setInterval(() => load({ silent: true }), POLL_MS);
    };
    const onVisibility = () => {
      if (document.hidden) return stop();
      load({ silent: true });
      start();
    };
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [sending, load]);

  /** Runs an action; on any outcome the campaign is reloaded, since its state may have changed. */
  const act = async (action) => {
    setBusy(true);
    try {
      await action();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
      setConfirm(null);
      await load({ silent: true });
      onChanged?.();
    }
  };

  const retry = (includeUncertain) =>
    act(async () => {
      setUncertainLeft(0);
      setUncertainMessage('');
      try {
        const result = includeUncertain
          ? await adminWhatsAppService.retryFailed(campaignId, { includeUncertain: true })
          : await adminWhatsAppService.retryFailed(campaignId);
        toast.success(`${result.requeued} retried`);
        if (result.uncertain_left) {
          setUncertainLeft(result.uncertain_left);
          setUncertainMessage(
            `${result.uncertain_left} could not be retried automatically because they may already have been delivered; check them before retrying.`,
          );
        }
      } catch (err) {
        // Every failed row was uncertain: the same "retry these too" action handles it, no toast.
        if (!includeUncertain && err.status === 409 && err.code === WHATSAPP_RETRY_FAILED_CODE.ONLY_UNCERTAIN) {
          setUncertainLeft(err.detail?.uncertain_left || 0);
          setUncertainMessage(err.message);
          return;
        }
        throw err;
      }
    });

  const cancel = () =>
    act(async () => {
      await adminWhatsAppService.cancelCampaign(campaignId);
      toast.success('Campaign cancelled');
    });

  const resume = () =>
    act(async () => {
      await adminWhatsAppService.resumeCampaign(campaignId);
      toast.success('Campaign resumed');
    });

  const backButton = (
    <button
      type="button"
      onClick={onBack}
      aria-label="Back to campaigns"
      className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-input rounded-lg hover:bg-muted/50"
    >
      <ArrowLeft size={16} /> Campaigns
    </button>
  );

  if (!detail) {
    return (
      <div className="space-y-4">
        {backButton}
        {error ? (
          <div className="p-6 text-center bg-card border border-border rounded-lg">
            <p className="font-medium text-foreground">Could not load this campaign</p>
            <p className="mt-1 text-sm text-muted-foreground">{error}</p>
            <button
              type="button"
              onClick={() => load()}
              className="mt-4 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
            >
              Try again
            </button>
          </div>
        ) : (
          <p className="py-10 text-center text-sm text-muted-foreground">Loading campaign...</p>
        )}
      </div>
    );
  }

  const counts = detail.counts || {};
  const canRetry =
    (counts[WHATSAPP_MESSAGE_STATUS.FAILED] || 0) > 0 && (detail.status === C.COMPLETED || detail.status === C.SENDING);
  const canCancel = detail.status === C.DRAFT || detail.status === C.SCHEDULED || detail.status === C.SENDING;
  const dialog = confirm ? CONFIRMS[confirm] : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        {backButton}
        <button
          type="button"
          onClick={() => load()}
          disabled={loading}
          aria-label="Refresh campaign"
          className="p-2 border border-input rounded-lg hover:bg-muted/50 disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="p-4 bg-card border border-border rounded-lg space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-medium text-foreground break-words">{detail.name}</h2>
            <p className="text-sm text-muted-foreground">
              {detail.template_name || 'Template removed'} - created {formatShortDate(detail.created_at)} -{' '}
              {detail.total} recipients
            </p>
          </div>
          <CampaignStatusBadge
            status={detail.status}
            scheduledAt={detail.scheduled_at}
            pausedReason={detail.paused_reason}
          />
        </div>

        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {CAMPAIGN_STATUS_COUNT_ORDER.map(({ status, label }) => (
            <div key={status} className="p-3 bg-muted/50 rounded-lg">
              <dt className="text-xs text-muted-foreground">{label}</dt>
              <dd className="text-lg font-medium text-foreground">{counts[status] || 0}</dd>
            </div>
          ))}
        </dl>

        {detail.paused_reason && (
          <div className="p-3 text-sm bg-orange-50 dark:bg-orange-950/50 border border-orange-200 dark:border-orange-900 rounded-lg space-y-2">
            <p className="text-orange-900 dark:text-orange-300 whitespace-pre-line">{detail.paused_reason}</p>
            <button
              type="button"
              disabled={busy}
              onClick={resume}
              className="px-3 py-2 text-sm border border-orange-300 dark:border-orange-800 rounded-lg bg-card hover:bg-orange-100 dark:hover:bg-orange-900/60 disabled:opacity-50"
            >
              Resume sending
            </button>
          </div>
        )}

        {(canRetry || canCancel) && (
          <div className="flex flex-wrap gap-2">
            {canRetry && (
              <button
                type="button"
                disabled={busy}
                onClick={() => retry(false)}
                className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
              >
                Retry failed
              </button>
            )}
            {canCancel && (
              <button
                type="button"
                disabled={busy}
                onClick={() => setConfirm('cancel')}
                className="px-4 py-2 text-sm border border-red-300 dark:border-red-800 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/70 disabled:opacity-50"
              >
                Cancel campaign
              </button>
            )}
          </div>
        )}

        {uncertainLeft > 0 && (
          <div className="p-3 text-sm bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-900 rounded-lg space-y-2">
            <p className="text-amber-900 dark:text-amber-300">{uncertainMessage}</p>
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirm('uncertain')}
              className="px-3 py-2 text-sm border border-amber-300 dark:border-amber-800 rounded-lg bg-card hover:bg-amber-100 dark:hover:bg-amber-900/60 disabled:opacity-50"
            >
              Retry these too (may send duplicates)
            </button>
          </div>
        )}
      </div>

      <RecipientsTable
        page={detail.recipients}
        statusFilter={statusFilter}
        busy={loading}
        onFilterChange={(status) => {
          setStatusFilter(status);
          setPage(1);
        }}
        onPageChange={setPage}
      />

      {dialog && (
        <Modal onClose={() => setConfirm(null)} maxWidth="md" fullHeight={false}>
          <div className="max-h-[80vh] overflow-y-auto p-6 space-y-4">
            <h3 className="text-lg font-medium text-foreground">{dialog.title}</h3>
            <p className="text-sm text-muted-foreground">{dialog.body}</p>
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirm(null)}
                className="px-4 py-2 text-sm border border-input rounded-lg hover:bg-muted/50"
              >
                {dialog.keep}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => (confirm === 'cancel' ? cancel() : retry(true))}
                className={`px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50 ${dialog.tone}`}
              >
                {dialog.go}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CampaignDetail;
